import {
  RealEstateChangeLogResponseChanges,
  RealEstateChangeLogResponseRTKQ
} from "@/src-refactor/modules/store/qrtk/services/realestate/values/changeLog.realestate.value";
import HCheckArray from "@rf-helper/check-have-value";
import dayjs from "dayjs";

const ProperChangeLogAdapter = (
  resp: RealEstateChangeLogResponseRTKQ["data"] | undefined
) => {
  if (!HCheckArray(resp) || typeof resp == "undefined") {
    return [];
  }

  return resp
    .map(change => ({
      author: change?.author?.name,
      date: dayjs(change.created_at).format("DD/MM/YYYY hh:mm A"),
      changes: getChanges(change.changes)
    }))
    .filter(x => x.changes.length > 0);
};

export default ProperChangeLogAdapter;

const getChanges = (
  changes: RealEstateChangeLogResponseChanges
): {
  type:
    | "arrow"
    | "no-soporte"
    | "deepArrow"
    | "addAndRemoveArrow"
    | "plainTextArrow";
  data: any;
  label: string;
}[] => {
  const arrowTypes: Record<string, string> = {
    price: "Precio",
    visit: "Visita",
    realstatetype: "Tipo de propiedad",
    sector: "Sector",
    sqm_land: "Metros de solar",
    maintenance_fee: "Precio de mantenimiento",
    status: "Estado",
    address: "Dirección",
    bedrooms: "Habitaciones",
    currency: "Tipo de moneda del proyecto",
    latitude: "Latitud",
    longitude: "Longitud",
    bathrooms: "Baños",
    arrangement: "Arreglo",
    is_collection: "¿Es una propiedad collection?",
    floors_total: "Total de pisos",
    floor_level: "Nivel de piso",
    reference: "Punto de referencia",
    parking_spots: "Parqueos",
    half_bathrooms: "Medios baños",
    expires: "Fecha de expiración",
    property_title_number: "Matrícula",
    sqm_construction: "Metros de construcción",
    visit_status: "¿Está habitado?",
    alternate_price: "Precio de alquiler",
    currency_alternate: "Tipo de moneda de alquiler",
    condition_status: "Estado del inmueble",
    is_no_broker: "Asociado",
    lease_commission: "Comisión por alquiler",
    guaranteed_comission: "Porcentaje de comisión del agente vendedor",
    total_comission: "Comisión total",
    has_loan: "¿Tiene hipoteca?",
    loan_note: "Nota de hipoteca",
    dropbox_link: "Enlace de Dropbox",
    google_drive_link: "Enlace de Google Drive",
    realstate_tour_url: "Enlace de tour virtual",
    delivers: "Fecha de entrega",
    projecttype: "Tipo de proyecto",
    business_type: "Tipo de negocio",
    business_detail: "Detalle de negocio",
    sale_price: "Precio de venta",
    rent_price: "Precio de alquiler",
    association_type: "Asociado",
    master_broker: "Master broker",
    separation_price: "Precio de separación",
    trust_status: "¿Tiene fideicomiso?",
    trust_entity: "Nombre de la fiduciaria",
    trust_meeting_date: "Fecha de reunión de fideicomiso",
    estimated_trust_formation_date: "Fecha estimada de constitución de fideicomiso",
    trust_email: "Correo de la fiduciaria",
    trust_officer_name: "Nombre del ejecutivo",
    has_transferred_title_builder: "Transferir título a la constructora",
    trust_rnc: "RNC de la fiduciaria",
    is_legal_status_definitive: "¿El estatus jurídico es definitivo?",
    project_company_name: "Nombre de la constructora",
    project_company_rnc: "RNC de la constructora",
    has_trust_meeting: "¿Tuvo una reunión con la fiduciaria?",
    meeting_trust_entity: "Nombre de la fiduciaria",
    meeting_officer_name: "Nombre del ejecutivo de la reunión",
    meeting_notes: "Notas de la reunión con la fiduciaria",
    has_confotur: "¿Tiene confotur?",
    name: "Nombre de la propiedad",
    property_title: "Título de la propiedad",
    sales_comission: "Comisión de venta",
    capture_comission: "Comisión de captación"
  };
  const arrowPlainTypes: Record<string, string> = {
    property_title_number: "Matrícula"
  };

  const items: {
    type:
      | "arrow"
      | "no-soporte"
      | "deepArrow"
      | "addAndRemoveArrow"
      | "plainTextArrow";
    data: any;
    label: string;
  }[] = [];

  // Ejecución segura de prepares: si uno lanza error se ignora el cambio
  const safePrepare = <T>(fn: (arg: any) => T, data: any): T | null => {
    try {
      return fn(data);
    } catch (error) {
      console.log("Error ejecutando prepare:", error, data);
      return null;
    }
  };

  Object.keys(changes).forEach(key => {
    // Special cases that need preparation
    switch (key) {
      case "info": {
        const prepared = safePrepare(prepareSeo, changes[key]);
        if (prepared !== null) {
          items.push({ type: "deepArrow", data: prepared, label: "Seo" });
        }
        break;
      }
      case "agents": {
        const prepared = safePrepare(prepareUser, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Agentes captadores"
          });
        }
        break;
      }
      case "documents": {
        const prepared = safePrepare(prepareDocuments, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Documentos"
          });
        }
        break;
      }
      case "units": {
        const prepared = safePrepare(prepareUnid, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Unidades"
          });
        }
        break;
      }
      case "buildings": {
        const prepared = safePrepare(prepareBuildings, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Etapas"
          });
        }
        break;
      }
      case "owners": {
        const prepared = safePrepare(prepareUser, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Propietarios"
          });
        }
        break;
      }
      case "improvements": {
        const prepared = safePrepare(prepareImprovements, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Mejoras"
          });
        }
        break;
      }
      case "images": {
        const prepared = safePrepare(prepareImages, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Imágenes"
          });
        }
        break;
      }
      case "validationDocuments": {
        const prepared = safePrepare(prepareValidationDocuments, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Validación de documento"
          });
        }
        break;
      }
      case "videos": {
        const prepared = safePrepare(prepareVideos, changes[key]);
        if (prepared !== null) {
          items.push({
            type: "addAndRemoveArrow",
            data: prepared,
            label: "Videos"
          });
        }
        break;
      }
      default:
        if (key in arrowPlainTypes) {
          items.push({
            type: "plainTextArrow",
            data: changes[key],
            label: arrowTypes[key]
          });
        } else if (key in arrowTypes) {
          items.push({ type: "arrow", data: changes[key], label: arrowTypes[key] });
        } else {
          items.push({ type: "no-soporte" as const, data: changes[key], label: "" });
        }
        break;
    }
  });

  return items.filter(x => x.type !== "no-soporte");
};

const prepareSeo = (data: any) => {
  const items = Object.keys(data).map((item, i) => ({
    title: item,
    subChanges: Object.keys(data[item])
      .map(x => {
        if (x == "title") {
          return { title: "Titulo", new: data[item][x].new, old: data[item][x].old };
        }
        if (x == "keywords") {
          // return {
          //   title: "Palabras clave",
          //   new: data[item][x].new,
          //   old: data[item][x].old
          // };
          return "skip";
        }
        if (x == "description") {
          return {
            title: "Descripción",
            new: data[item][x].new,
            old: data[item][x].old
          };
        }
        if (x == "short_description") {
          // return {
          //   title: "Descripción corta",
          //   new: data[item][x].new,
          //   old: data[item][x].old
          // };
          return "skip";
        } else return { type: "no-soporte" as const, data: null, title: "" };
      })
      .filter(x => x !== "skip")
  }));
  return items;
};

const prepareUser = (data: any) => {
  return Object.keys(data).map(e => ({
    items: data[e].map((x: any) => x.name).join(", "),
    action: e
  }));
};

const prepareDocuments = (data: any) => {
  return Object.keys(data).map(e => ({
    items: data[e]?.join(", ") || "",
    action: e
  }));
};

const prepareValidationDocuments = (data: any) => {
  return Object.keys(data).map(e => ({
    items: Object.keys(data[e])
      .map(x => data[e][x]?.name)
      .join(", "),
    action: e
  }));
};

const prepareVideos = (data: any) => {
  return Object.keys(data).map(e => ({
    items: data[e].map((x: string) => x).join(", "),
    action: e
  }));
};
const prepareUnid = (data: any) => {
  return Object.keys(data).map(e => ({
    items: data[e]
      .map((x: any) => `Cod:${x?.id}-${x?.name || "Sin nombre"}`)
      .join(", "),
    action: e
  }));
};

const prepareBuildings = (data: any) => {
  return Object.keys(data).map(e => ({
    items: data[e]
      .map((x: any) => `Cod:${x?.id}-${x?.description || "Sin nombre"}`)
      .join(", "),
    action: e
  }));
};
const prepareImages = (data: any) => {
  return Object.keys(data).map(e => ({
    items: data[e],
    action: e
  }));
};

const prepareImprovements = (data: any) => {
  const joinEqualCategoryItems = (
    items: { feature_name: string; value_name: string }[]
  ) => {
    return items.reduce((acc, curr) => {
      const existing = acc.find(item => item.feature_name === curr.feature_name);
      if (existing) {
        existing.value_name += `, ${curr.value_name}`;
      } else {
        acc.push({ ...curr });
      }
      return acc;
    }, [] as { feature_name: string; value_name: string }[]);
  };

  return Object.keys(data).map(e => ({
    items: joinEqualCategoryItems(data[e])
      .map(x => `${x.feature_name} > ${x.value_name}`)
      .join(", "),
    action: e
  }));
};
